/**
 * Standortmodell der marktbox.
 *
 * Bewertet einen geplanten Automaten-Standort und liefert Score, Umsatzkorridor,
 * Break-even, Modell-Empfehlung und die größten Hebel.
 *
 * Wie amortisation.model.ts ist diese Datei eine reine, seiteneffektfreie
 * Rechen-Bibliothek ohne Angular-Bezug – nutzbar im Frontend (Angular) UND im
 * Backend (Node), z. B. zum Vorqualifizieren von Leads.
 *
 * WICHTIG: Das Break-even wird NICHT neu gerechnet. Der Score erzeugt einen
 * erwarteten Monatsumsatz; diesen Umsatz übergibt das Modell an das bestehende
 * `calculateAmortisation` aus amortisation.model.ts. So sprechen Standort- und
 * Amortisationsrechner garantiert dieselbe Sprache. Wer dort eine Zahl ändert,
 * ändert sie auch hier.
 */

import {
  AMORTISATION_CONFIG,
  PRESET_AVERAGE_CUSTOMER,
  calculateAmortisation,
} from './amortisation.model';

// ---------------------------------------------------------------------------
// Eingaben
// ---------------------------------------------------------------------------

/** 'eigenstaendig' = freier Standort · 'filiale' = vor dem eigenen Laden */
export type Modus = 'eigenstaendig' | 'filiale';

export type ArtStandort = 'hof' | 'wohn' | 'tank' | 'camping' | 'gewerbe';
export type StrasseTyp = 'anlieger' | 'ortsdurchfahrt' | 'landstrasse' | 'hauptstrasse';
export type Sichtbarkeit = 'strasse' | 'hof' | 'abseits';
export type Parken = 'davor' | 'wenige' | 'schwierig';
export type LehDistanz = 'ueber5' | '1bis5' | 'unter1';

export interface StandortInput {
  modus: Modus;
  art: ArtStandort;
  strasse: StrasseTyp;
  sicht: Sichtbarkeit;
  parken: Parken;
  /** Entfernung zum nächsten Lebensmittelhandel – nur relevant im Modus 'eigenstaendig' */
  leh: LehDistanz;
  /** Haushalte im Umkreis von 2 km */
  haushalte: number;
  /** Nur relevant im Modus 'filiale' */
  sonntagGeoeffnet: boolean;
}

/** Startzustand des Rechners – ein solider freier Standort. */
export const DEFAULT_STANDORT_INPUT: StandortInput = {
  modus: 'eigenstaendig',
  art: 'hof',
  strasse: 'anlieger',
  sicht: 'strasse',
  parken: 'davor',
  leh: 'ueber5',
  haushalte: 1000,
  sonntagGeoeffnet: false,
};

// ---------------------------------------------------------------------------
// Gewichte & Faktor-Kennlinien
//
// PLATZHALTER: Die relativen Gewichte sind fundierte Startwerte. Sie sollen aus
// den Standortmerkmalen der 450 Bestandsboxen abgeleitet werden (Umfrage +
// WebApp-Umsätze). Das Prinzip bleibt gleich, nur die Zahlen werden genauer.
// ---------------------------------------------------------------------------

/** Modus 'eigenstaendig' – die Versorgungslücke (leh) ist der dominante Faktor. */
export const WEIGHTS_EIGENSTAENDIG = {
  leh: 0.32,
  haushalte: 0.2,
  strasse: 0.16,
  sicht: 0.12,
  parken: 0.1,
  art: 0.1,
} as const;

/** Modus 'filiale' – ohne Versorgungslücke (der eigene Laden IST die Lage), renormiert. */
export const WEIGHTS_FILIALE = {
  haushalte: 0.3,
  strasse: 0.24,
  sicht: 0.18,
  parken: 0.15,
  art: 0.13,
} as const;

export const FACTOR_SCORES = {
  leh: { ueber5: 1.0, '1bis5': 0.55, unter1: 0.15 },
  strasse: { anlieger: 0.3, ortsdurchfahrt: 0.7, landstrasse: 0.85, hauptstrasse: 0.35 },
  sicht: { strasse: 1.0, hof: 0.6, abseits: 0.25 },
  parken: { davor: 1.0, wenige: 0.7, schwierig: 0.3 },
  art: { hof: 0.6, wohn: 0.7, tank: 0.85, camping: 0.9, gewerbe: 0.5 },
} as const;

/**
 * Verschlusszeiten-Faktor (Modus 'filiale'): Anteil der Nachfrage, den der
 * Automat außerhalb der Ladenöffnung noch abgreifen kann. Nicht linear nach
 * Stunden, sondern nach Nachfrage gewichtet – das Sonntags-Fenster erklärt den
 * größten Teil der Varianz.
 */
export const VERSCHLUSS_FAKTOR = { sonntagGeschlossen: 0.55, sonntagGeoeffnet: 0.35 } as const;

/**
 * Score -> erwarteter Monatsumsatz.
 * Anker: der reale Flottendurchschnitt (PRESET_AVERAGE_CUSTOMER.revenue, aktuell
 * 2.076 €) liegt bei Score 52. Unterhalb des Durchschnitts fällt der Umsatz
 * bewusst deutlich ab – schwache Standorte sollen realistisch niedrig liegen.
 * Beobachtete Flotten-Spanne 600–4.800 €; der Boden 400 € lässt hypothetisch
 * schwache Plätze zu.
 */
export const REVENUE_MODEL = {
  anchorScore: 52,
  anchorRevenue: PRESET_AVERAGE_CUSTOMER.revenue, // = 2076
  slopePerPoint: 58,
  min: 400,
  max: 4800,
} as const;

// ---------------------------------------------------------------------------
// Ergebnis
// ---------------------------------------------------------------------------

export type StufeKey = 'sehr_gut' | 'solide' | 'kritisch';

export interface Stufe {
  key: StufeKey;
  label: string;
  sub: string;
}

export interface Hebel {
  title: string;
  text: string;
}

export interface Empfehlung {
  modell: 'kauf' | 'miete';
  titel: string;
  text: string;
}

export interface StandortResult {
  score: number; // 0..100
  stufe: Stufe;
  revenue: { low: number; mid: number; high: number };
  /** Monate; null, wenn die laufenden Kosten den Rohertrag übersteigen. */
  breakevenMonths: number | null;
  monthlySurplus: number;
  withinAfa: boolean;
  empfehlung: Empfehlung;
  hebel: Hebel[];
  /** Nur im Modus 'filiale': Gegenüberstellung Filiale vs. freier Standort. */
  vergleich?: {
    filiale: { score: number; revenueMid: number };
    frei: { score: number; revenueMid: number };
    faktor: number; // frei.mid / filiale.mid
  };
}

// ---------------------------------------------------------------------------
// Reine Helfer
// ---------------------------------------------------------------------------

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
const round10 = (x: number) => Math.round(x / 10) * 10;
const haushalteScore = (h: number) => Math.min(1, h / 2500);

export function revenueFromScore(score: number): number {
  const { anchorRevenue, anchorScore, slopePerPoint, min, max } = REVENUE_MODEL;
  return clamp(anchorRevenue + slopePerPoint * (score - anchorScore), min, max);
}

export function corridor(score: number): { low: number; mid: number; high: number } {
  const mid = revenueFromScore(score);
  return {
    low: Math.max(REVENUE_MODEL.min, round10(mid * 0.82)),
    mid,
    high: Math.min(REVENUE_MODEL.max, round10(mid * 1.2)),
  };
}

export function stufeOf(score: number): Stufe {
  if (score >= 70)
    return {
      key: 'sehr_gut',
      label: $localize`:@@standort.stufe.sehrGut:Sehr gut`,
      sub: $localize`:@@standort.stufe.sehrGut.sub:Ein starker Standort – hier stimmt die Versorgungslücke.`,
    };
  if (score >= 45)
    return {
      key: 'solide',
      label: $localize`:@@standort.stufe.solide:Solide`,
      sub: $localize`:@@standort.stufe.solide.sub:Trägt sich – mit gezielten Verbesserungen wird mehr draus.`,
    };
  return {
    key: 'kritisch',
    label: $localize`:@@standort.stufe.kritisch:Kritisch`,
    sub: $localize`:@@standort.stufe.kritisch.sub:So noch schwierig – aber es gibt konkrete Hebel. Kein verlorener Fall.`,
  };
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

interface FactorValues {
  leh: number;
  haushalte: number;
  strasse: number;
  sicht: number;
  parken: number;
  art: number;
}

function factorValues(input: StandortInput): FactorValues {
  return {
    leh: FACTOR_SCORES.leh[input.leh],
    haushalte: haushalteScore(input.haushalte),
    strasse: FACTOR_SCORES.strasse[input.strasse],
    sicht: FACTOR_SCORES.sicht[input.sicht],
    parken: FACTOR_SCORES.parken[input.parken],
    art: FACTOR_SCORES.art[input.art],
  };
}

export function scoreEigenstaendig(input: StandortInput): number {
  const f = factorValues(input);
  let s = 0;
  for (const k of Object.keys(WEIGHTS_EIGENSTAENDIG) as (keyof typeof WEIGHTS_EIGENSTAENDIG)[]) {
    s += f[k] * WEIGHTS_EIGENSTAENDIG[k];
  }
  return Math.round(s * 100);
}

export function scoreFiliale(input: StandortInput): number {
  const f = factorValues(input);
  let base = 0;
  for (const k of Object.keys(WEIGHTS_FILIALE) as (keyof typeof WEIGHTS_FILIALE)[]) {
    base += f[k] * WEIGHTS_FILIALE[k];
  }
  base *= 100;
  const vf = input.sonntagGeoeffnet
    ? VERSCHLUSS_FAKTOR.sonntagGeoeffnet
    : VERSCHLUSS_FAKTOR.sonntagGeschlossen;
  return Math.round(base * vf);
}

/** Vergleichsszenario: derselbe Umkreis, aber ein freier Standort ohne LEH. */
export function freeScenarioScore(input: StandortInput): number {
  return scoreEigenstaendig({
    ...input,
    modus: 'eigenstaendig',
    leh: 'ueber5',
    strasse: 'landstrasse',
    sicht: 'strasse',
    parken: 'davor',
    art: 'hof',
  });
}

// ---------------------------------------------------------------------------
// Break-even (delegiert an das Amortisationsmodell)
// ---------------------------------------------------------------------------

/** Kipppunkt Kauf/Miete in Monaten – umsatzunabhängig (Kaufpreis / Miete). */
export const KIPP_MONTHS = AMORTISATION_CONFIG.price / AMORTISATION_CONFIG.rent;

function amortisationFor(revenue: number) {
  // Gleiche Annahmen wie der Amortisationsrechner (Durchschnittskunde),
  // nur der Umsatz kommt aus dem Standort-Score.
  return calculateAmortisation({ ...PRESET_AVERAGE_CUSTOMER, revenue });
}

// ---------------------------------------------------------------------------
// Hebel (kein Ergebnis ist eine Sackgasse)
// ---------------------------------------------------------------------------

const HEBEL: Record<keyof FactorValues, Hebel> = {
  leh: {
    title: $localize`:@@standort.hebel.leh.title:Abstand zum nächsten Supermarkt vergrößern.`,
    text: $localize`:@@standort.hebel.leh.text:Der wichtigste Hebel überhaupt: Ein Platz mit über 5 km zum nächsten Lebensmittelhandel hebt den Score am stärksten. Prüfen Sie Standorte im Umland statt im Ort.`,
  },
  haushalte: {
    title: $localize`:@@standort.hebel.haushalte.title:Umfeld mit mehr Haushalten wählen.`,
    text: $localize`:@@standort.hebel.haushalte.text:Ein Standort mit dichterer Bebauung im 2-km-Umkreis bringt mehr Stammkundschaft. Manchmal reichen wenige hundert Meter Richtung Wohngebiet.`,
  },
  strasse: {
    title: $localize`:@@standort.hebel.strasse.title:An eine Straße mit mehr Durchgangsverkehr.`,
    text: $localize`:@@standort.hebel.strasse.text:Eine Ortsdurchfahrt oder Landstraße bringt Laufkundschaft, die eine ruhige Anliegerstraße nicht liefert.`,
  },
  sicht: {
    title: $localize`:@@standort.hebel.sicht.title:Sichtbarkeit verbessern.`,
    text: $localize`:@@standort.hebel.sicht.text:Den Automaten direkt an die Straße rücken oder klar ausschildern – aus Fahrtrichtung erkennbar, bevor man vorbei ist.`,
  },
  parken: {
    title: $localize`:@@standort.hebel.parken.title:Halten ermöglichen.`,
    text: $localize`:@@standort.hebel.parken.text:Eine Haltebucht oder ein, zwei Stellplätze direkt am Automaten. Ohne Halt kein Kauf – oft der günstigste große Hebel.`,
  },
  art: {
    title: $localize`:@@standort.hebel.art.title:Standorttyp überdenken.`,
    text: $localize`:@@standort.hebel.art.text:Ein Platz mit klarem Halt-Anlass (Tankstelle, Campingplatz, Ortsdurchfahrt) wandelt Interesse besser in Käufe.`,
  },
};

const MAX_FACTOR: FactorValues = { leh: 1.0, haushalte: 1.0, strasse: 0.85, sicht: 1.0, parken: 1.0, art: 0.9 };

function topHebelEigenstaendig(f: FactorValues): Hebel[] {
  const gains = (Object.keys(WEIGHTS_EIGENSTAENDIG) as (keyof typeof WEIGHTS_EIGENSTAENDIG)[])
    .map((k) => ({ k, gain: (MAX_FACTOR[k] - f[k]) * WEIGHTS_EIGENSTAENDIG[k] * 100 }))
    .filter((g) => g.gain > 0.8)
    .sort((a, b) => b.gain - a.gain);
  return gains.slice(0, 2).map((g) => HEBEL[g.k]);
}

// ---------------------------------------------------------------------------
// Öffentliche Hauptfunktion
// ---------------------------------------------------------------------------

export function evaluateStandort(input: StandortInput): StandortResult {
  const isFiliale = input.modus === 'filiale';
  const f = factorValues(input);

  const score = isFiliale ? scoreFiliale(input) : scoreEigenstaendig(input);
  const rev = corridor(score);
  const amort = amortisationFor(rev.mid);
  const kipp = Math.round(KIPP_MONTHS);

  // Hebel
  let hebel: Hebel[];
  let vergleich: StandortResult['vergleich'];
  if (isFiliale) {
    const freeScore = freeScenarioScore(input);
    const freeRev = corridor(freeScore);
    hebel = [
      {
        title: $localize`:@@standort.hebel.zweitstandort.title:Ein freier Zweitstandort im Umland.`,
        text: $localize`:@@standort.hebel.zweitstandort.text:Vor der Filiale verdient der Automat nur außerhalb Ihrer Öffnungszeiten. Ein freier Standort ohne Supermarkt im Umkreis kommt erfahrungsgemäß auf das Zwei- bis Dreifache – der größte Hebel für Sie.`,
      },
    ];
    const second = topHebelEigenstaendig(f).find((h) => h !== HEBEL.leh);
    if (second) hebel.push(second);
    if (input.sonntagGeoeffnet) {
      hebel.push({
        title: $localize`:@@standort.hebel.sonntag.title:Das Sonntags-Fenster.`,
        text: $localize`:@@standort.hebel.sonntag.text:Weil Sie sonntags geöffnet haben, entgeht dem Automaten sein stärkstes Zeitfenster. Ein Standort abseits der Filiale löst das.`,
      });
    }
    vergleich = {
      filiale: { score, revenueMid: rev.mid },
      frei: { score: freeScore, revenueMid: freeRev.mid },
      faktor: rev.mid > 0 ? freeRev.mid / rev.mid : 0,
    };
  } else {
    hebel = topHebelEigenstaendig(f);
    if (hebel.length === 0) {
      hebel.push({
        title: $localize`:@@standort.hebel.keine.title:Ihr Standort nutzt die wichtigsten Hebel bereits.`,
        text: $localize`:@@standort.hebel.keine.text:Der nächste sinnvolle Schritt ist nicht am Standort, sondern beim Break-even: Prüfen Sie im Amortisationsrechner, nach wie vielen Monaten sich Kauf oder Miete rechnet.`,
      });
    }
  }

  // Modell-Empfehlung – dieselbe Logik wie im Amortisationsrechner (Kipppunkt)
  const be = amort.breakevenMonths;
  const beText =
    be !== null
      ? $localize`:@@standort.empfehlung.beMonate:ca. ${formatMonths(be)}:MONATE: Monaten`
      : $localize`:@@standort.empfehlung.beNie:über der Abschreibungsdauer`;
  let empfehlung: Empfehlung;
  if (isFiliale) {
    empfehlung = {
      modell: 'miete',
      titel: $localize`:@@standort.empfehlung.filiale.titel:Empfehlung Modell: Miete (300 € netto/Monat).`,
      text: $localize`:@@standort.empfehlung.filiale.text:Vor der eigenen Filiale verdient der Automat nur außerhalb der Öffnungszeiten – der planbare Zusatzumsatz passt besser zu monatlichen Raten. Der Umsatz entsteht ohne zusätzliche Personalkosten und ist damit margenstärker als über die Theke. Zur Einordnung: Ab rund ${kipp}:KIPP: Monaten Amortisation lohnt der Kauf mehr als die Miete – ein freier Standort erreicht das meist deutlich schneller.`,
    };
  } else if (be !== null && be < KIPP_MONTHS) {
    empfehlung = {
      modell: 'kauf',
      titel: $localize`:@@standort.empfehlung.kauf.titel:Empfehlung Modell: Kauf (6.800 € netto).`,
      text: $localize`:@@standort.empfehlung.kauf.text:Bei diesem Umsatz amortisiert sich der Automat rechnerisch nach ${beText}:BE_TEXT: – schneller als der Kipppunkt von rund ${kipp}:KIPP: Monaten, ab dem der Kauf die Miete schlägt. Danach arbeitet er voll für Sie.`,
    };
  } else {
    empfehlung = {
      modell: 'miete',
      titel: $localize`:@@standort.empfehlung.eherMiete.titel:Empfehlung Modell: eher Miete (300 € netto/Monat).`,
      text: $localize`:@@standort.empfehlung.eherMiete.text:Die rechnerische Amortisation liegt bei ${beText}:BE_TEXT: – nahe oder über dem Kipppunkt von rund ${kipp}:KIPP: Monaten. Solange der Standort nicht stärker wird, ist die Miete das kleinere Risiko. Die Hebel oben zeigen, wie Sie den Umsatz heben.`,
    };
  }

  return {
    score,
    stufe: stufeOf(score),
    revenue: rev,
    breakevenMonths: be,
    monthlySurplus: amort.monthlySurplus,
    withinAfa: amort.withinAfa,
    empfehlung,
    hebel,
    vergleich,
  };
}

// ---------------------------------------------------------------------------
// Formatierung & CRM
// ---------------------------------------------------------------------------

export function formatMonths(m: number): string {
  return m.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}

export function formatEuro(x: number): string {
  return `${Math.round(x).toLocaleString('de-DE')} €`;
}

/**
 * Attribute für Brevo/CRM – passend zum Briefing (eine Liste, Steuerung über
 * Attribute). standort_status: vorhanden | filiale. score_stufe wie StufeKey.
 */
export function toCrmAttributes(input: StandortInput, result: StandortResult): {
  standort_status: 'vorhanden' | 'filiale';
  score_stufe: StufeKey;
} {
  return {
    standort_status: input.modus === 'filiale' ? 'filiale' : 'vorhanden',
    score_stufe: result.stufe.key,
  };
}
